/**
 * Service de Notifications Push
 * Gere l'enregistrement, les preferences et l'envoi de notifications
 */

const VAPID_PUBLIC_KEY = window.VAPID_PUBLIC_KEY || '';

// Convertit la cle VAPID en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Verifie si les notifications push sont supportees
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Verifie si les notifications sont autorisees
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Demande la permission pour les notifications
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Enregistre le Service Worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker non supporte');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('[Notifications] Service Worker enregistre:', registration.scope);

    // Attendre que le SW soit actif
    if (registration.installing) {
      await new Promise((resolve) => {
        registration.installing.addEventListener('statechange', (e) => {
          if (e.target.state === 'activated') {
            resolve();
          }
        });
      });
    }

    return registration;
  } catch (error) {
    console.error('[Notifications] Erreur enregistrement SW:', error);
    return null;
  }
}

/**
 * S'abonne aux notifications push
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications non supportees');
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Permission refusee');
  }

  const registration = await navigator.serviceWorker.ready;

  // Verifier si deja abonne
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription && VAPID_PUBLIC_KEY) {
    // Creer un nouvel abonnement
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
  }

  return subscription;
}

/**
 * Se desabonne des notifications push
 */
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await subscription.unsubscribe();
    return true;
  }
  return false;
}

/**
 * Envoie l'abonnement au serveur
 */
export async function sendSubscriptionToServer(subscription) {
  try {
    const response = await fetch('/api/fast/notifications/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        subscription: subscription.toJSON()
      })
    });

    if (!response.ok) {
      throw new Error('Erreur serveur');
    }

    return await response.json();
  } catch (error) {
    console.error('[Notifications] Erreur envoi abonnement:', error);
    throw error;
  }
}

/**
 * Recupere les preferences de notification de l'utilisateur
 */
export async function getNotificationPreferences() {
  try {
    const response = await fetch('/api/fast/notifications/preferences');
    if (!response.ok) {
      return { push_enabled: false, email_enabled: false };
    }
    return await response.json();
  } catch (error) {
    console.error('[Notifications] Erreur recuperation preferences:', error);
    return { push_enabled: false, email_enabled: false };
  }
}

/**
 * Sauvegarde les preferences de notification
 */
export async function saveNotificationPreferences(preferences) {
  try {
    const response = await fetch('/api/fast/notifications/preferences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(preferences)
    });

    if (!response.ok) {
      throw new Error('Erreur sauvegarde');
    }

    return await response.json();
  } catch (error) {
    console.error('[Notifications] Erreur sauvegarde preferences:', error);
    throw error;
  }
}

/**
 * Affiche une notification locale (via Service Worker)
 */
export async function showLocalNotification(title, options = {}) {
  const permission = getNotificationPermission();
  if (permission !== 'granted') {
    console.warn('[Notifications] Permission non accordee');
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    // Fallback: notification native
    new Notification(title, options);
    return true;
  }

  const registration = await navigator.serviceWorker.ready;

  // Envoyer un message au SW pour afficher la notification
  if (registration.active) {
    registration.active.postMessage({
      type: 'SHOW_NOTIFICATION',
      title,
      options
    });
    return true;
  }

  return false;
}

/**
 * Notifie la fin d'une generation (image ou video)
 */
export async function notifyGenerationComplete(type, success, details = {}) {
  const prefs = JSON.parse(localStorage.getItem('notification_prefs') || '{}');

  // Verifier les preferences
  if (!prefs.push_enabled && !prefs.email_enabled) {
    return;
  }

  const title = success
    ? `${type === 'image' ? 'Image' : 'Video'} generee`
    : `Echec generation ${type}`;

  const body = success
    ? details.message || `Votre ${type} est prete !`
    : details.error || 'Une erreur est survenue';

  // Notification push locale
  if (prefs.push_enabled) {
    await showLocalNotification(title, {
      body,
      icon: '/images/icon-192.png',
      tag: `generation-${type}-${Date.now()}`,
      data: {
        type: 'generation',
        mediaType: type,
        url: details.url || `/${type === 'image' ? 'nano' : 'video'}`
      }
    });
  }

  // Notification email (via serveur)
  if (prefs.email_enabled && success) {
    try {
      await fetch('/api/fast/notifications/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'generation_complete',
          media_type: type,
          details
        })
      });
    } catch (error) {
      console.error('[Notifications] Erreur envoi email:', error);
    }
  }
}

/**
 * Initialise le systeme de notifications
 */
export async function initNotifications() {
  // Enregistrer le Service Worker
  await registerServiceWorker();

  // Charger les preferences depuis le localStorage
  const savedPrefs = localStorage.getItem('notification_prefs');
  if (savedPrefs) {
    return JSON.parse(savedPrefs);
  }

  return {
    push_enabled: false,
    email_enabled: false
  };
}

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  sendSubscriptionToServer,
  getNotificationPreferences,
  saveNotificationPreferences,
  showLocalNotification,
  notifyGenerationComplete,
  initNotifications
};
